import { SetupTaskId } from '../setupTasksData'
import { SetupStep } from './d'
import { $RootStore } from '../../../../src/stores/RootStore'
import { WebPageVisibility } from '../../../../graphQL/generated/enums'

export const BookingSystemAdvancedStepIds = {
  bookingReminders: `${SetupTaskId.BookingSystemAdvanced}-bookingReminders`,
  consultationForms: `${SetupTaskId.BookingSystemAdvanced}-consultationForms`,
  cancellationPolicy: `${SetupTaskId.BookingSystemAdvanced}-cancellationPolicy`,
  termsAndConditions: `${SetupTaskId.BookingSystemAdvanced}-termsAndConditions`,
  aftercare: `${SetupTaskId.BookingSystemAdvanced}-aftercare`,
  notificationPreferences: `${SetupTaskId.BookingSystemAdvanced}-notificationPreferences`,
} as const

const bookingSystemAdvancedSteps: SetupStep[] = [
  {
    title: 'Booking client updates',
    id: BookingSystemAdvancedStepIds.bookingReminders,
    taskId: SetupTaskId.BookingSystemAdvanced,
    description: 'Keep your clients up to date with their bookings via SMS and email to reduce no-shows and last-minute confusion.',
    icon: 'notifications',
    check: (_rootStore: $RootStore) => {
      // Platform-specific check defined in stepData.ts
      return false
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      rootStore.uiStore.seenPrompts = rootStore.uiStore.seenPrompts.filter(prompt => prompt !== BookingSystemAdvancedStepIds.bookingReminders)
    },
    toastData: {
      title: 'Great! You can enable as many booking updates as you like.',
      nextStepId: SetupTaskId.BookingSystemAdvanced,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Consultation forms',
    id: BookingSystemAdvancedStepIds.consultationForms,
    taskId: SetupTaskId.BookingSystemAdvanced,
    description: 'Collect important client information before appointments to save time and deliver a more personalised service.',
    icon: 'consultationForm',
    check: (rootStore: $RootStore) => {
      // Own forms only — a salon's forms shouldn't tick off the member's setup step
      return rootStore.consultationFormStore.ownItems.length > 0
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      await rootStore.communication.requester.deleteSaasData({ models: ['ConsultationForm'] })
    },
    toastData: {
      title: 'Consultation form set up complete.',
      nextStepId: SetupTaskId.BookingSystemAdvanced,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Cancellation policy',
    id: BookingSystemAdvancedStepIds.cancellationPolicy,
    taskId: SetupTaskId.BookingSystemAdvanced,
    description: 'Set clear cancellation rules to protect your time and reduce lost revenue.',
    icon: 'cancellation',
    check: (rootStore: $RootStore) => {
      return rootStore.setupStore.didAction === 'setupCancellationPolicy'
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      rootStore.setupStore.didAction = undefined
    },
    toastData: {
      title: 'Added a cancellation policy',
      nextStepId: SetupTaskId.BookingSystemAdvanced,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Terms & conditions',
    id: BookingSystemAdvancedStepIds.termsAndConditions,
    taskId: SetupTaskId.BookingSystemAdvanced,
    description: 'Add your business terms to ensure clients understand your policies and expectations.',
    icon: 'terms',
    check: (rootStore: $RootStore) => {
      return rootStore.setupStore.didAction === 'addedTermsAndConditions'
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      rootStore.setupStore.didAction = undefined
    },
    toastData: {
      title: 'Added your terms & conditions',
      nextStepId: SetupTaskId.BookingSystemAdvanced,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Aftercare instructions',
    id: BookingSystemAdvancedStepIds.aftercare,
    taskId: SetupTaskId.BookingSystemAdvanced,
    description: 'Automatically send aftercare guidance to help clients get the best results and improve repeat bookings.',
    icon: 'info',
    check: (rootStore: $RootStore) => {
      const aftercarePage = rootStore.webPageStore.webPageByPath('aftercare')
      return aftercarePage?.data.visibility === WebPageVisibility.Visible
        && rootStore.setupStore.didAction === 'addedAftercareInfo'
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      rootStore.setupStore.didAction = undefined
    },
    toastData: {
      title: 'Added aftercare information',
      nextStepId: SetupTaskId.BookingSystemAdvanced,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Notification preferences',
    id: BookingSystemAdvancedStepIds.notificationPreferences,
    taskId: SetupTaskId.BookingSystemAdvanced,
    description: 'Choose which alerts you receive so you stay informed without being overwhelmed.',
    icon: 'notifications',
    skipOnPlatforms: ['SD'], // notifications in mobile only
    check: (rootStore: $RootStore) => {
      return rootStore.setupStore.didAction === 'setupNotificationPreferences'
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      rootStore.setupStore.didAction = undefined
    },
    toastData: {
      title: 'Notification preferences updated',
      nextStepId: SetupTaskId.BookingSystemAdvanced,
    },
    dependsOnStepIds: [],
  },
]

export default bookingSystemAdvancedSteps
